import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { ListingMapbox } from '@/components/Listing/Map/ListingMapbox';
import { ListingContainerTabs } from '@/components/Listing/Container/Tabs';
import { SearchBarForm } from '@/components/SearchBarForm';
import { setSearchParamsInUrl } from '@/components/helpers/queryParam';
import { HousingPosting, UnifiedCard } from './UnifiedCard';

interface HousingContainerProps {
  signedIn: boolean;
  school: any;
  pois: any[];
  searchParams: any;
}

interface PaginationMetadata {
  page: number;
  pages: number;
  count: number;
  prev: number | null;
  next: number | null;
}

export function HousingContainer({ signedIn, school, pois, searchParams }: HousingContainerProps) {
  const [loading, setLoading] = useState(true);
  const [hasLoaded, setHasLoaded] = useState(false);
  const [searchVersion, setSearchVersion] = useState(0);
  const [postings, setPostings] = useState<HousingPosting[]>([]);
  const [listingsByCoordinates, setListingsByCoordinates] = useState<Record<string, any[]>>({});
  const [pagination, setPagination] = useState<PaginationMetadata | null>(null);
  const [params, setParams] = useState({ types: 'listing,roommate,classified', ...searchParams });
  const [listView, setListView] = useState(true);
  const listingSelected = (params.types || 'listing,roommate,classified').split(',').indexOf('listing') >= 0;

  const fetchHousing = async (nextParams: any, page = 1) => {
    const requestParams = { ...nextParams, page };
    setLoading(true);
    setParams(requestParams);
    setSearchParamsInUrl(requestParams);
    if ((requestParams.types || '').split(',').indexOf('listing') < 0) setListView(true);

    const [housingResponse, favoritesResponse, blocksResponse] = await Promise.all([
      axios.get('/api/housing', { params: requestParams }),
      axios.get('/api/favorites'),
      axios.get('/api/blocks'),
    ]);
    const favoriteKeys = new Set<string>(favoritesResponse.data);
    const blockKeys = new Set<string>(blocksResponse.data);
    const visiblePostings: HousingPosting[] = housingResponse.data.postings.filter((posting: HousingPosting) => {
      const key = `${posting.type.charAt(0).toUpperCase()}${posting.type.slice(1)}:${posting.data.id}`;
      posting.data.favorited = favoriteKeys.has(key);
      return !blockKeys.has(key);
    });
    const coordinates: Record<string, any[]> = {};
    visiblePostings
      .filter((posting) => posting.type === 'listing')
      .forEach((posting) => {
        const listing = posting.data;
        const key = `${listing.longitude}, ${listing.latitude}`;
        coordinates[key] ||= [];
        coordinates[key].push(listing);
      });

    setPostings(visiblePostings);
    setListingsByCoordinates(coordinates);
    setPagination(housingResponse.data.pagination);
    setSearchVersion((version) => version + 1);
    setLoading(false);
    setHasLoaded(true);
  };

  useEffect(() => {
    fetchHousing(params, Number(searchParams.page) || 1);
  }, []);

  const removePosting = (posting: HousingPosting) => () => {
    setPostings((current) => current.filter((item) => item.id !== posting.id));
    setPagination((current) => (current ? { ...current, count: Math.max(0, current.count - 1) } : current));
    if (posting.type === 'listing') {
      setListingsByCoordinates((current) => {
        const coordinates = { ...current };
        const key = `${posting.data.longitude}, ${posting.data.latitude}`;
        coordinates[key] = (coordinates[key] || []).filter((listing) => listing.id !== posting.data.id);
        if (!coordinates[key].length) delete coordinates[key];
        return coordinates;
      });
      setSearchVersion((version) => version + 1);
    }
  };

  return (
    <div className="map-listings">
      <SearchBarForm listView={listView} searchParams={searchParams} submitHandler={fetchHousing} />
      {!hasLoaded ? (
        <p className="lead text-center pt-5">
          <i className="fa fa-circle-o-notch fa-spin fs-2 mb-2" />
          <br />
          Loading...
        </p>
      ) : (
        <div style={{ position: 'relative' }}>
          {loading && (
            <div
              className="listings-loading-spinner"
              style={{
                position: 'fixed',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                zIndex: 1000,
                background: 'rgba(255,255,255,.95)',
                borderRadius: '.5rem',
                padding: '.75rem 1.25rem',
              }}
              role="status"
            >
              <i className="fa fa-circle-o-notch fa-spin fs-2" />
              <span className="sr-only">Loading…</span>
            </div>
          )}
          <div style={{ opacity: loading ? 0.4 : 1, pointerEvents: loading ? 'none' : 'auto' }}>
            <ListingContainerTabs listView={listView} setListView={setListView} mapEnabled={listingSelected} />
            <div style={{ display: listView ? 'block' : 'none' }}>
              <p className="lead">
                {pagination?.count || 0} {(pagination?.count || 0) === 1 ? 'result' : 'results'} found
              </p>
              {postings.length ? (
                <div className="row row-cols-1 row-cols-sm-2 row-cols-md-3 row-cols-lg-4">
                  {postings.map((posting) => (
                    <UnifiedCard
                      key={posting.id}
                      posting={posting}
                      signedIn={signedIn}
                      onBlocked={removePosting(posting)}
                    />
                  ))}
                </div>
              ) : (
                <p className="lead text-center">Sorry, no housing posts match those criteria.</p>
              )}
              {pagination && pagination.pages > 1 && (
                <nav aria-label="Housing results pages">
                  <ul className="pagination justify-content-center">
                    <li className={`page-item ${pagination.prev ? '' : 'disabled'}`}>
                      <button
                        className="page-link"
                        disabled={!pagination.prev}
                        onClick={() => fetchHousing(params, pagination.prev || 1)}
                      >
                        ‹ Prev
                      </button>
                    </li>
                    <li className="page-item disabled">
                      <span className="page-link">
                        Page {pagination.page} of {pagination.pages}
                      </span>
                    </li>
                    <li className={`page-item ${pagination.next ? '' : 'disabled'}`}>
                      <button
                        className="page-link"
                        disabled={!pagination.next}
                        onClick={() => fetchHousing(params, pagination.next || pagination.page)}
                      >
                        Next ›
                      </button>
                    </li>
                  </ul>
                </nav>
              )}
            </div>
            <div style={{ visibility: listView ? 'hidden' : 'visible' }}>
              <ListingMapbox
                listings={listingsByCoordinates}
                schoolLatLong={{ lat: school.latitude, lng: school.longitude }}
                pois={pois}
                refreshKey={searchVersion}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
